import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { auth, db } from "../firebaseConfig";

import "../styles/generalStyle.css";
import "./Letras.css";
import { useLanguage } from "../i18n/LanguageContext";

export default function LetraDetailsPage() {
  const { letraId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const [firebaseUser, setFirebaseUser] = useState(() => auth.currentUser);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const [titulo, setTitulo] = useState("");
  const [letra, setLetra] = useState("");
  const [estilo, setEstilo] = useState(""); // opcional si existe

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setFirebaseUser(u || null));
    return () => unsub();
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!firebaseUser || !letraId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const ref = doc(db, "usuarios", firebaseUser.uid, "letras", letraId);
        const snap = await getDoc(ref);

        if (!snap.exists()) {
          setError(t("letras.details.notFound"));
          setLoading(false);
          return;
        }

        const data = snap.data();
        setTitulo(data.titulo || "");
        setLetra(data.letra || "");
        setEstilo(data.estilo || "");
      } catch (e) {
        console.error("Error cargando letra:", e);
        setError(t("letras.details.errorLoad"));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [firebaseUser, letraId, t]);

  const canSave = useMemo(() => {
    return !!firebaseUser && !saving && (titulo.trim() || letra.trim());
  }, [firebaseUser, saving, titulo, letra]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(letra || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (e) {
      console.error("copy failed", e);
      setError(t("letras.details.copyError"));
    }
  };

  const handleSave = async () => {
    if (!firebaseUser) return;

    try {
      setSaving(true);
      setError("");

      const ref = doc(db, "usuarios", firebaseUser.uid, "letras", letraId);

      await updateDoc(ref, {
        titulo: titulo.trim(),
        letra: letra,
        // estilo no lo tocamos aquí; si quieres editarlo luego lo agregamos
      });

      // pequeño feedback
      setCopied(false);
    } catch (e) {
      console.error("Error guardando letra:", e);
      setError(t("letras.details.errorSave"));
    } finally {
      setSaving(false);
    }
  };

  if (!firebaseUser && !loading) {
    return (
      <div className="page">
        <div className="container">
          <div className="card">
            <h1 className="h1">{t("auth.needLoginTitle")}</h1>
            <p className="p-muted" style={{ marginTop: 8 }}>
              {t("auth.needLoginDesc")}
            </p>
            <button className="btn btn-primary" style={{ marginTop: 14 }} onClick={() => navigate("/login")}>
              {t("auth.goLogin")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page">
        <div className="container">
          <div className="card">
            <div className="profile-loading" style={{ marginTop: 0 }}>
              <div className="spinner" />
              <p>{t("letras.details.loading")}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="container">
        <div className="letrasDetailsTop">
          <button className="btn" onClick={() => navigate("/app/letras")}>
            {t("letras.details.back")}
          </button>

          <div className="letrasDetailsActions">
            <button className="btn" onClick={handleCopy} type="button">
              {copied ? t("letras.details.copied") : t("letras.details.copy")}
            </button>

            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={!canSave}
              type="button"
            >
              {saving ? t("letras.details.saving") : t("letras.details.save")}
            </button>
          </div>
        </div>

        <div className="grid-2">
          {/* Editor */}
          <section className="card">
            <h1 className="h1">
              <span className="letrasTitleIcon" aria-hidden>📝</span>
              {t("letras.details.title")}
            </h1>

            <div className="form" style={{ marginTop: 10 }}>
              <label className="label">
                {t("letras.details.fieldTitle")}
                <input
                  className="input"
                  value={titulo}
                  onChange={(e) => setTitulo(e.target.value)}
                  maxLength={80}
                  placeholder={t("letras.details.titlePlaceholder")}
                />
              </label>

              {/* Estilo solo informativo si existe */}
              <div className="hintBox" style={{ marginTop: 4 }}>
                <p className="hintText" style={{ margin: 0 }}>
                  {t("letras.details.styleLabel")}{" "}
                  <b>{estilo?.trim() ? estilo : "—"}</b>
                </p>
              </div>

              <label className="label" style={{ marginTop: 6 }}>
                {t("letras.details.fieldLyrics")}
                <textarea
                  className="input textarea"
                  value={letra}
                  onChange={(e) => setLetra(e.target.value)}
                  rows={12}
                  placeholder={t("letras.details.lyricsPlaceholder")}
                />
              </label>

              {error ? <div className="alert-error">{error}</div> : null}
            </div>
          </section>

          {/* Preview */}
          <section className="card">
            <h2 className="h2">{t("letras.details.previewTitle")}</h2>
            <p className="p-muted">{t("letras.details.previewDesc")}</p>

            <div className="previewBox">
              <div className="previewTitle">
                {titulo?.trim() ? titulo.trim() : t("letras.item.untitled")}
              </div>
              <pre className="previewText">{letra || t("letras.details.noLyricsYet")}</pre>
            </div>

            <p className="note" style={{ marginTop: 10 }}>
              {t("letras.details.note")}
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
